import React, { FC, forwardRef, useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

import requiredFileValidator from '../../../validators/requiredFileValidator';
import minFilesValidator from '../../../validators/minFilesValidator';
import maxFilesValidator from '../../../validators/maxFilesValidator';
import filterPropsByComponent from '../../../utils/components/_filterPropsByComponent';
import Control from '../Control';
import { UploadControlComposedProps, UploadControlDefaultProps, UploadControlValidationProps } from './UploadControl';

const withControl = <Value = (File | string)[], StateValue = File[]>(WrappedComponent: FC<UploadControlComposedProps<Value, StateValue>>) => {
  const WithControl = forwardRef<HTMLInputElement, UploadControlComposedProps<Value, StateValue>>(({ required, minFiles, maxFiles, ...props }, ref) => {
    const defaultValidators = useMemo(() => ({
      required: ['requiredFile', requiredFileValidator],
      minFiles: minFilesValidator,
      maxFiles: maxFilesValidator,
    }), []);

    const TypedControl = Control<Value, StateValue, HTMLInputElement, UploadControlValidationProps>;

    return (
      <TypedControl
        {...filterPropsByComponent(props, TypedControl)}
        required={required}
        minFiles={minFiles}
        maxFiles={maxFiles}
        defaultValidators={defaultValidators}
        allowInvalid
      >
        {({
          id, value, isValid, hasWarning, hasCaution, hasInfo, isTouched, errorMessagesId, onValueChange: onValueChangeCallback, onTouched: onTouchedCallback,
        }) => (
          <WrappedComponent
            id={id}
            data-control
            {...props}
            value={value}
            isValid={isValid}
            hasWarning={hasWarning}
            hasCaution={hasCaution}
            hasInfo={hasInfo}
            isTouched={isTouched}
            aria-errormessage={errorMessagesId}
            ref={ref}
            required={required}
            onValueChangeCallback={onValueChangeCallback}
            onTouchedCallback={onTouchedCallback}
          />
        )}
      </TypedControl>
    );
  });

  WithControl.defaultProps = UploadControlDefaultProps;

  return hoistNonReactStatics(WithControl, WrappedComponent);
};

export default withControl;
